import { HugeiconsIcon } from '@hugeicons/react'
import * as Popover from '@radix-ui/react-popover'
import Fuse from 'fuse.js/min-basic'
import clsx from 'clsx'
import { Tick01Icon, ArrowDown01Icon } from '@hugeicons/core-stroke-rounded'
import { Cancel01Icon, Search01Icon } from '@hugeicons/core-solid-rounded'
import { useLingui } from '@lingui/react/macro'

import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  Combobox,
  ComboboxItem,
  ComboboxList,
  ComboboxProvider
} from '@ariakit/react'

import Loading from './Loading'

interface Option<T extends string | number> {
  value: T
  label: string
}

interface Props<T extends string | number> {
  options: Option<T>[]
  value?: T[]
  name?: string
  onChange: (newValues: T[]) => void
  ariaLabel?: string
  placeholder?: string
  invalid?: boolean
  searchText?: string
  loading?: boolean
  search?: string
  onChangeSearch?: (newSearch: string) => void
  maxSelections?: number
  readOnly?: boolean
}

export default function MultiComboBox<T extends string | number>({
  options,
  invalid,
  name,
  value = [],
  ariaLabel,
  placeholder,
  searchText,
  onChange,
  loading = false,
  search,
  onChangeSearch,
  maxSelections,
  readOnly
}: Props<T>) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const [internalSearchValue, setInternalSearchValue] = useState('')

  const searchValue = search ?? internalSearchValue

  const optionsFuse = useMemo(() => {
    return new Fuse(options, {
      keys: ['label', 'value'],
      threshold: 0.3
    })
  }, [options])

  const matches = useMemo(() => {
    if (!searchValue) return options
    return optionsFuse.search(searchValue).map(result => result.item)
  }, [searchValue, optionsFuse, options])

  const selectedOptions = useMemo(() => {
    return options.filter(opt => value.includes(opt.value))
  }, [options, value])

  const onUpdateSearch = useCallback(
    (val: string) => {
      if (onChangeSearch) onChangeSearch(val)
      else setInternalSearchValue(val)
    },
    [onChangeSearch]
  )

  const toggleSelection = useCallback(
    (optionValue: T) => {
      if (value.includes(optionValue)) {
        onChange(value.filter(v => v !== optionValue))
      } else {
        if (maxSelections && value.length >= maxSelections) return
        onChange([...value, optionValue])
      }
    },
    [value, onChange, maxSelections]
  )

  const removeSelection = useCallback(
    (optionValue: T) => {
      onChange(value.filter(v => v !== optionValue))
    },
    [value, onChange]
  )

  const clearAll = useCallback(() => {
    onChange([])
  }, [onChange])

  useEffect(() => {
    if (!open) onUpdateSearch('')
  }, [open, onUpdateSearch])

  const valueLength = value.length

  if (readOnly) {
    return (
      <div className="flex flex-wrap gap-1 items-center py-2">
        {value.length === 0 ? (
          <span className="text-text-light">
            {placeholder || t`None selected`}
          </span>
        ) : (
          selectedOptions.map(option => (
            <span
              key={String(option.value)}
              className="inline-flex items-center gap-1 bg-primary-bright rounded px-2 py-0.5 text-sm"
            >
              {option.label}
            </span>
          ))
        )}
      </div>
    )
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <ComboboxProvider
        open={open}
        setOpen={setOpen}
        resetValueOnHide
        includesBaseElement={false}
        setValue={val => startTransition(() => onUpdateSearch(val))}
      >
        <div className="relative w-full">
          <Popover.Trigger asChild>
            <button
              type="button"
              aria-label={ariaLabel}
              name={name}
              className={clsx(
                'w-full flex select-none flex-row items-center gap-1 rounded-small border border-text bg-white py-1.5 px-2 pr-4 text-left min-h-[2.5rem] cursor-pointer hover:opacity-100',
                value.length > 0 && 'pr-8',
                invalid && 'border-red-500'
              )}
            >
              <HugeiconsIcon
                icon={ArrowDown01Icon}
                className="size-4 text-text flex-shrink-0"
              />
              <div className="flex-1 flex flex-wrap gap-1 items-center">
                {value.length === 0 ? (
                  <span className="text-text-light">{placeholder}</span>
                ) : (
                  selectedOptions.map(option => {
                    const optionLabel = option.label

                    return (
                      <span
                        key={option.value}
                        className="inline-flex items-center gap-1 bg-primary-bright rounded px-2 py-0.5 text-sm"
                      >
                        {option.label}
                        <span
                          onClick={e => {
                            e.stopPropagation()
                            removeSelection(option.value)
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.stopPropagation()
                              removeSelection(option.value)
                            }
                          }}
                          tabIndex={0}
                          className="hover:opacity-50 cursor-pointer focus:outline-1 focus:outline-primary"
                          aria-label={t`Remove ${optionLabel}`}
                        >
                          <HugeiconsIcon
                            icon={Cancel01Icon}
                            className="size-3"
                          />
                        </span>
                      </span>
                    )
                  })
                )}
              </div>
            </button>
          </Popover.Trigger>
          {value.length > 0 && (
            <button
              type="button"
              tabIndex={0}
              onClick={clearAll}
              className="flex flex-row justify-center items-center p-1 size-5 absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer"
              aria-label={t`Clear all`}
            >
              <HugeiconsIcon
                icon={Cancel01Icon}
                className="size-full text-text"
              />
            </button>
          )}
        </div>

        <Popover.Content
          role="dialog"
          aria-label={ariaLabel}
          sideOffset={8}
          align="start"
          className="max-h-[var(--radix-popover-content-available-height)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex flex-col gap-1 rounded-small bg-white shadow-md outline-hidden overflow-hidden z-popover"
        >
          <div className="flex flex-row items-center gap-2.5 p-3">
            <HugeiconsIcon icon={Search01Icon} />
            <Combobox
              autoSelect
              placeholder={searchText || t`Search…`}
              className="w-full rounded-small border border-text bg-white text-regular text-text placeholder:text-text-light outline-hidden py-2 px-4 focus:outline-solid focus:outline-primary"
              onBlurCapture={event => {
                event.preventDefault()
                event.stopPropagation()
              }}
            />
          </div>
          <ComboboxList className="flex flex-col gap-2 overflow-y-scroll p-3">
            {loading && (
              <div className="flex flex-row w-full p-5 justify-center items-center">
                <Loading />
              </div>
            )}
            {!loading && matches.length === 0 && (
              <div className="text-text-light text-center py-4">
                {t`No results found`}
              </div>
            )}
            {matches.map(({ label, value: optionValue }) => {
              const isSelected = value.includes(optionValue)
              const isDisabled =
                !isSelected && maxSelections && value.length >= maxSelections

              return (
                <ComboboxItem
                  key={String(optionValue)}
                  value={String(optionValue)}
                  onClick={event => {
                    event.preventDefault()

                    toggleSelection(optionValue)
                  }}
                  disabled={!!isDisabled}
                  className={clsx(
                    'relative group flex cursor-pointer flex-row items-center gap-2 rounded-small p-2 pl-8 data-[active-item]:bg-primary-bright hover:bg-primary-bright focus:bg-primary-bright focus:outline-hidden',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isSelected && (
                    <span className="absolute left-2 top-1/2 -translate-y-1/2">
                      <HugeiconsIcon
                        icon={Tick01Icon}
                        className="text-text size-4"
                      />
                    </span>
                  )}
                  <span>{label}</span>
                </ComboboxItem>
              )
            })}
          </ComboboxList>
          {maxSelections && (
            <div className="text-xs text-text-light px-3 pb-2">
              {t`${valueLength} / ${maxSelections} selected`}
            </div>
          )}
        </Popover.Content>
      </ComboboxProvider>
    </Popover.Root>
  )
}
